import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { getAccountDetails } from "../services/hubspot";

export interface Portal {
  id: string;
  status: "connected" | "needs_review" | "disconnected";
  name: string;
  hubDomain: string;
  timeZone: string;
  nickname?: string;
}

interface ApiPortal {
  id: string;
  name: string;
  hubspot_portal_id: string;
  connected_by: string;
  created_at: number;
}

interface PortalContextValue {
  portals: Portal[];
  renamePortal: (id: string, nickname: string) => void;
}

const PortalContext = createContext<PortalContextValue | null>(null);

export function PortalProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [portals, setPortals] = useState<Portal[]>([]);

  useEffect(() => {
    Promise.all([
      fetch("/api/portals").then((r) => {
        if (r.status === 401) {
          navigate("/login");
          return [];
        }
        return r.json() as Promise<ApiPortal[]>;
      }),
      fetch("/api/nicknames").then(
        (r) => r.json() as Promise<Record<string, string>>,
      ),
    ])
      .then(([apiPortals, nicknames]) => {
        const mapped: Portal[] = apiPortals.map((p) => ({
          id: p.hubspot_portal_id,
          status: "connected" as const,
          name: p.name || p.hubspot_portal_id,
          hubDomain: "",
          timeZone: "",
          nickname: nicknames[p.hubspot_portal_id],
        }));
        setPortals(mapped);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    for (const portal of portals) {
      getAccountDetails(portal.id)
        .then((details) => {
          setPortals((prev) =>
            prev.map((p) =>
              p.id === portal.id
                ? {
                    ...p,
                    name: details.hub_domain || p.name,
                    hubDomain: details.hub_domain,
                    timeZone: details.timeZone,
                  }
                : p,
            ),
          );
        })
        .catch(() => {});
    }
  }, [portals.length]);

  function renamePortal(id: string, nickname: string) {
    const value = nickname || undefined;
    setPortals((prev) =>
      prev.map((p) => (p.id === id ? { ...p, nickname: value } : p)),
    );
    fetch("/api/nicknames", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [id]: nickname || "" }),
    }).catch(() => {});
  }

  return (
    <PortalContext.Provider value={{ portals, renamePortal }}>
      {children}
    </PortalContext.Provider>
  );
}

export function usePortalContext(): PortalContextValue {
  const ctx = useContext(PortalContext);
  if (!ctx)
    throw new Error("usePortalContext must be used within PortalProvider");
  return ctx;
}

export function usePortals(): Portal[] {
  return usePortalContext().portals;
}
